import React from 'react';
import {
    TextField,
    MenuItem,
    Button,
    Grid,
    CircularProgress,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO, format, isValid } from 'date-fns';


export interface FormField {
    name: string;
    label: string;
    type: 'text' | 'number' | 'email' | 'select' | 'date';
    required: boolean | ((values: any) => boolean);
    options?: { label: string; value: string | number }[];
    gridProps?: { xs?: number; sm?: number; md?: number };
    visibleIf?: (values: any) => boolean;
    confirmReset?: boolean;
    dependencies?: string[];
    maxLength?: number;
}

interface SchemaFormProps {
    schema: FormField[];
    onSubmit: (data: any) => Promise<void> | void;
    submitLabel?: string;
    defaultValues?: any;
    validationSchema?: any;
}

const SchemaForm: React.FC<SchemaFormProps> = ({
    schema,
    onSubmit,
    submitLabel = 'Submit',
    defaultValues = {},
    validationSchema,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        trigger,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues,
        resolver: validationSchema ? zodResolver(validationSchema) : undefined,
        mode: 'onTouched',
    });

    // State for reset confirmation dialog
    const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
    const [pendingReset, setPendingReset] = React.useState<{ name: string; value: any } | null>(null);

    // Watch all values to handle conditional visibility and dynamic required
    const values = useWatch({ control });

    // Helper to check if a field is required based on current form values
    const isFieldRequired = (field: FormField): boolean => {
        if (typeof field.required === 'function') {
            return field.required(values);
        }
        return !!field.required;
    };

    // Check if there are any form-level errors (root errors from superRefine)
    const hasErrors = Object.keys(errors).length > 0;


    const handleFormSubmit = async (data: any) => {
        try {
            // Trim all string values before submitting
            const trimmedData = Object.fromEntries(
                Object.entries(data).map(([key, val]) => [
                    key,
                    typeof val === 'string' ? val.trim() : val,
                ])
            );
            await onSubmit(trimmedData);
        } catch (error) {
            console.error('Form submission error', error);
        }
    };

    const handleClear = () => {
        reset(defaultValues);
    };

    const handleFieldChange = (field: FormField, newValue: any, originalOnChange: (...event: any[]) => void) => {
        if (field.confirmReset) {
            setPendingReset({ name: field.name, value: newValue });
            setResetDialogOpen(true);
        } else {
            originalOnChange(newValue);
            if (field.dependencies && field.dependencies.length > 0) {
                trigger(field.dependencies as any);
            }
        }
    };

    const handleConfirmReset = () => {
        if (pendingReset) {
            // Reset form to default values, but apply the new value for the triggering field
            reset({
                ...defaultValues,
                [pendingReset.name]: pendingReset.value,
            });
            setResetDialogOpen(false);
            setPendingReset(null);
        }
    };

    const handleCancelReset = () => {
        setResetDialogOpen(false);
        setPendingReset(null);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <>
                <Dialog
                    open={resetDialogOpen}
                    onClose={handleCancelReset}
                    PaperProps={{
                        sx: {
                            borderRadius: '24px',
                            padding: '8px',
                            backgroundColor: '#ffffff',
                            maxWidth: '420px',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            textAlign: 'center',
                            color: '#132F5F',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '20px',
                            fontWeight: 600,
                            lineHeight: '28px',
                            pb: 1,
                        }}
                    >
                        Reset Form?
                    </DialogTitle>
                    <DialogContent>
                        <Typography
                            sx={{
                                color: '#49454F',
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                textAlign: 'center',
                                lineHeight: '22px',
                            }}
                        >
                            Changing this field will reset the form and clear all other data. Do you want to continue?
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            padding: '12px 24px 20px',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '12px',
                        }}
                    >
                        <Button
                            onClick={handleCancelReset}
                            sx={{
                                color: '#FF6D5A',
                                border: '1px solid #E3E2E2',
                                borderRadius: '16px',
                                textTransform: 'none',
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                px: 4,
                                py: 1.25,
                                '&:hover': {
                                    border: '1px solid #FF6D5A',
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmReset}
                            variant="contained"
                            autoFocus
                            sx={{
                                backgroundColor: '#0A1D56',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 600,
                                px: 4,
                                py: 1.25,
                                '&:hover': {
                                    backgroundColor: '#091B4C',
                                },
                            }}
                        >
                            Confirm & Reset
                        </Button>
                    </DialogActions>
                </Dialog>

                <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                    {/* Form-level error summary banner */}
                    {hasErrors && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <Typography variant="body2" fontWeight={600}>
                                Please fix the following errors before submitting:
                            </Typography>
                            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                {Object.entries(errors).map(([key, error]: [string, any]) => (
                                    <li key={key}>
                                        <Typography variant="body2">
                                            {error?.message || 'Invalid input'}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                    <Grid container spacing={3}>
                        {schema.map((field) => {
                            // Check visibility
                            if (field.visibleIf && !field.visibleIf(values)) {
                                return null;
                            }

                            return (
                                <Grid
                                    key={field.name}
                                    size={{
                                        xs: field.gridProps?.xs || 12,
                                        sm: field.gridProps?.sm || 12,
                                        md: field.gridProps?.md || 12,
                                    }}
                                >
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
                                            const required = isFieldRequired(field);
                                            const fieldLabel = required ? `${field.label} *` : field.label;

                                            const errorSx = error ? {
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                                                    '&:hover fieldset': { borderColor: '#d32f2f' },
                                                    '&.Mui-focused fieldset': { borderColor: '#d32f2f' },
                                                },
                                                '& .MuiFormHelperText-root': {
                                                    color: '#d32f2f',
                                                    fontWeight: 500,
                                                },
                                            } : undefined;

                                            // Convert string value to Date for DatePicker
                                            const dateValue = field.type === 'date' && value
                                                ? (() => { const d = parseISO(value); return isValid(d) ? d : null; })()
                                                : null;

                                            return (
                                                <>
                                                    {field.type === 'date' ? (
                                                        <DatePicker
                                                            label={fieldLabel}
                                                            value={dateValue}
                                                            onChange={(newDate: Date | null) => {
                                                                const formatted = newDate && isValid(newDate)
                                                                    ? format(newDate, 'yyyy-MM-dd')
                                                                    : '';
                                                                handleFieldChange(field, formatted, onChange);
                                                            }}
                                                            disabled={isSubmitting}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    inputRef: ref,
                                                                    onBlur: onBlur,
                                                                    error: !!error,
                                                                    helperText: error ? error.message : null,
                                                                    variant: 'outlined',
                                                                    sx: {
                                                                        ...errorSx,
                                                                        '& .MuiOutlinedInput-root': {
                                                                            borderRadius: '12px',
                                                                            backgroundColor: '#fff',
                                                                            '& fieldset': {
                                                                                borderColor: error ? '#d32f2f' : '#E5E7EB',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: error ? '#d32f2f' : '#D1D5DB',
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: error ? '#d32f2f' : '#132F5F',
                                                                            },
                                                                        },
                                                                        '& .MuiInputLabel-root': {
                                                                            color: '#6B7280',
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    ) : field.type === 'select' ? (
                                                        <TextField
                                                            select
                                                            fullWidth
                                                            label={fieldLabel}
                                                            inputRef={ref}
                                                            value={value || ''}
                                                            onChange={(e) => handleFieldChange(field, e.target.value, onChange)}
                                                            onBlur={onBlur}
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                            variant="outlined"
                                                            disabled={isSubmitting}
                                                            sx={errorSx}
                                                        >
                                                            {field.options?.map((option) => (
                                                                <MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    ) : (
                                                        <TextField
                                                            fullWidth
                                                            type={field.type}
                                                            label={fieldLabel}
                                                            inputRef={ref}
                                                            value={value || ''}
                                                            onChange={(e) => handleFieldChange(field, e.target.value, onChange)}
                                                            onBlur={(e) => {
                                                                if (typeof e.target.value === 'string') {
                                                                    const trimmed = e.target.value.trim();
                                                                    if (trimmed !== e.target.value) {
                                                                        onChange(trimmed);
                                                                    }
                                                                }
                                                                onBlur();
                                                            }}
                                                            error={!!error}
                                                            helperText={error ? error.message : null}
                                                            variant="outlined"
                                                            disabled={isSubmitting}
                                                            sx={errorSx}
                                                            inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
                                                        />
                                                    )}
                                                </>
                                            );
                                        }}
                                    />
                                </Grid>
                            );
                        })}

                        <Grid size={{ xs: 12 }} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                                sx={{
                                    textTransform: 'none',
                                    backgroundColor: '#2D4677',
                                    '&:hover': {
                                        backgroundColor: '#1a365d',
                                    },
                                    minWidth: '120px',
                                    height: '48px',
                                    fontWeight: 600
                                }}
                            >
                                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : submitLabel}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleClear}
                                disabled={isSubmitting}
                                sx={{
                                    textTransform: 'none',
                                    backgroundColor: '#2D4677',
                                    '&:hover': {
                                        backgroundColor: '#1a365d',
                                    },
                                    minWidth: '120px',
                                    height: '48px',
                                    fontWeight: 600
                                }}
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </>
        </LocalizationProvider>
    );
};

export default SchemaForm;
